/** @jsx isolateComponent */
import React from 'react';
import { css } from '@emotion/core';
import { clamp } from 'lodash';
import { Animation, ColorName, Colors } from '@zapier/design-system/src/theme';
import { isolateComponent, labelStyles } from '@zapier/common-utils';

type ProgressBarProps = {
  /**
   * The color of the progress bar.
   * @default 'blue500'
   */
  color?: ColorName;
  /**
   * The maximum value for the progress bar.
   * @default 1
   */
  max?: number;
  /**
   * The minimum value for the progress bar.
   * @default 0
   */
  min?: number;
  /**
   * The amount of the progress bar. Should be between `min` and `max`.
   */
  value: number;
};

const Styles = labelStyles(__filename, {
  root: () =>
    css({
      position: 'relative',
      height: 5,
      borderRadius: 2.5,
      backgroundColor: Colors.grey200,
    }),
  progress: (color: ColorName) => [
    css({
      height: 'inherit',
      borderRadius: 'inherit',
      position: 'absolute',
      top: 0,
      left: 0,
      maxWidth: '100%',
      transition: `all ${Animation.transitionDuration} ease-in-out`,
      backgroundColor: Colors[color],
    }),
  ],
});

/**
 * Basic progress component. Shows how full something is.
 *
 * Tags: core-ui
 */
const ProgressBar: React.FC<ProgressBarProps> = ({
  color = 'blue500',
  max = 1,
  min = 0,
  ...props
}) => {
  const value = clamp(props.value, min, max);
  const range = max - min;
  const decimal = (value - min) / range;
  const width = `${decimal * 100}%`;
  return (
    <div css={Styles.root()}>
      <div
        css={Styles.progress(color)}
        data-testid="progress"
        style={{
          width,
        }}
      />
    </div>
  );
};

export default ProgressBar;
